import React, { useState } from 'react';
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/styles.css";
import { Card, Typography, Skeleton, IconButton, Divider, Box } from '@mui/material';
import CardMedia from '@mui/material/CardMedia';
import ReviewButton from './ReviewButton';
import SearchIcon from '@mui/icons-material/Search';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import CircularProgress from '@mui/material/CircularProgress';

export default function AlbumCards({ photo, wrapperStyle, renderDefaultPhoto, imageProps, isAdmin, isReview, isVideoOnly, isAutoplay, isFetching, searchParams, setSearchParams, smartSearchParams, setSmartSearchParams }) {
    const [loading, setLoading] = useState(true);
    const [errorCount, setErrorCount] = useState(0); // 用于限制重试次数
    const [mediaFailed, setMediaFailed] = useState(false); // 彻底失败时显示占位

    // 内联 SVG fallback，避免新增静态文件
    const FALLBACK_SVG = encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'>
            <text x='50%' y='50%' fill='#ccc' font-size='120' font-family='Arial' text-anchor='middle' dy='.3em'>
                Media unavailable
            </text>
        </svg>`
    );
    const FALLBACK_URL = `data:image/svg+xml;utf8,${FALLBACK_SVG}`;

    // 滚回顶部再触发动作
    const scrollToTopThen = (cb, timeout = 800) => {
        if (typeof window === 'undefined') return cb?.();

        const target = 0;
        // 已经在顶部
        if (Math.abs(window.scrollY - target) < 2) {
            return cb?.();
        }

        window.scrollTo({ top: target, behavior: 'smooth' });

        const start = performance.now();
        const check = () => {
            // 当接近目标或超过超时时间则认为滚动完成
            if (Math.abs(window.scrollY - target) < 2 || (performance.now() - start) > timeout) {
                cb?.();
            } else {
                requestAnimationFrame(check);
            }
        };
        requestAnimationFrame(check);
    };

    return (
        <Card style={{ position: "relative", ...wrapperStyle }}>
            <CardMedia>
                {loading && (
                    <Skeleton
                        animation="wave"
                        variant="rectangular"
                        width="100%"
                        height={photo?.height}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                    />
                )}

                {photo.type === "video" ? (
                    // 如果视频失败，我们允许一次尝试用 origin（若存在），然后降级为静态占位图
                    !mediaFailed ? (
                        <video
                            {...imageProps}
                            style={{ width: '100%', opacity: loading ? 0 : 1 }}
                            muted={!isAutoplay ? true : true}
                            autoPlay={!isAutoplay ? false : true}
                            loop
                            playsInline
                            referrerPolicy='no-referrer'
                            onLoadedData={() => setLoading(false)}
                            onError={(e) => {
                                console.warn('Video load error:', e, photo);
                                setLoading(false);
                                const target = e?.target;
                                // 尝试一次 origin（若存在且未尝试过）
                                if (errorCount === 0 && photo?.origin && target?.src !== photo.origin) {
                                    setErrorCount(1);
                                    try {
                                        target.onerror = null;
                                        target.src = photo.origin;
                                        target.load && target.load();
                                    } catch (err) {
                                        console.warn('Retry with origin failed', err);
                                        setMediaFailed(true);
                                    }
                                } else {
                                    // 最终降级为静态占位
                                    setMediaFailed(true);
                                }
                            }}
                        />
                    ) : (
                        // 视频彻底失败时显示占位（使用 photo.origin 或内联 fallback）
                        <img
                            alt={photo.username || 'media unavailable'}
                            src={photo?.origin || FALLBACK_URL}
                            style={{ width: '100%', opacity: 1 }}
                            referrerPolicy='no-referrer'
                            onLoad={() => setLoading(false)}
                            onError={(e) => {
                                console.warn('Fallback image error:', e, photo);
                                setLoading(false);
                                e.target.onerror = null;
                                e.target.src = FALLBACK_URL;
                            }}
                        />
                    )
                ) : (
                    // 图片逻辑：先显示原始 src，失败时尝试一次 origin，再降级为内联 fallback，防止无限循环
                    <img
                        alt={photo.username || 'media unavailable'}
                        {...imageProps}
                        style={{ width: '100%', opacity: loading ? 0 : 1 }}
                        referrerPolicy='no-referrer'
                        onLoad={() => setLoading(false)}
                        onError={(e) => {
                            console.warn('Image load error:', e, photo);
                            setLoading(false);
                            const target = e?.target;
                            target.onerror = null;
                            // 如果还有未尝试的 origin，则尝试一次
                            if (errorCount === 0 && photo?.origin && target?.src !== photo.origin) {
                                setErrorCount(1);
                                try {
                                    target.src = photo.origin;
                                } catch (err) {
                                    console.warn('Retry with origin failed', err);
                                    target.src = FALLBACK_URL;
                                }
                            } else {
                                target.src = FALLBACK_URL;
                            }
                        }}
                    />
                )}

                {/* isAdmin && isReview => 管理员审核按键 */}
                {photo.username && isAdmin && isReview && (
                    <div
                        style={{
                            position: "absolute",
                            backgroundColor: "rgba(0 0 0 / .4)",
                            inset: "auto 0 0 0",
                            padding: 4,
                        }}
                    >
                        <Typography component={'div'} >
                            <ReviewButton item={photo.item} />
                        </Typography>
                    </div>
                )}

                {/* 普通用户 => 画师名Label + 点击检索 */}
                {((photo.username && !isAdmin) || (photo.username && isAdmin && !isReview)) && (
                    <div
                        style={{
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: "rgba(0 0 0 / .4)",
                            inset: "auto 0 0 0",
                            padding: '4px 6px',
                            maxHeight: 28,
                        }}
                    >
                        {/* username 使用伸缩项并启用省略号 */}
                        <Typography
                            color={'white'}
                            variant='body2'
                            key={photo.src}
                            noWrap
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                // paddingLeft: 8,
                            }}
                        >
                            {photo.username}
                        </Typography>

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ bgcolor: 'white', opacity: 0.6 }}
                        />

                        <div style={{
                            display: 'flex',
                            gap: 4,
                            alignItems: 'center',
                            paddingLeft: 4
                        }}>
                            <IconButton
                                onClick={() => {
                                    scrollToTopThen(() => {
                                        setSearchParams(photo.userid);
                                    }, 900);
                                }}
                                size="small"
                                sx={{ height: 20, width: 20 }}
                                disabled={isFetching}
                            >
                                <Box sx={{ display: 'flex', size: 16 }}>
                                    {isFetching || loading ?
                                        <CircularProgress size={16} sx={{ color: 'white' }} />
                                        :
                                        <SearchIcon sx={{ fontSize: 16, color: 'white' }} />
                                    }
                                </Box>
                            </IconButton>
                            <IconButton
                                onClick={() => {
                                    scrollToTopThen(() => {
                                        setSmartSearchParams(photo.assetsid);
                                    }, 900);
                                }}
                                size="small"
                                sx={{ height: 20, width: 20 }}
                                disabled={isFetching}
                            >
                                <Box sx={{ display: 'flex', size: 16 }}>
                                    {isFetching || loading ?
                                        <CircularProgress size={16} sx={{ color: 'white' }} />
                                        :
                                        <ImageSearchIcon sx={{ fontSize: 16, color: 'white' }} />
                                    }
                                </Box>
                            </IconButton>
                        </div>
                    </div>
                )}

            </CardMedia>
        </Card>
    )
}